import { useEffect, useRef, useState } from 'react'
import { Drumstick, Droplets, Hand, Heart, Sparkles, TriangleAlert, Zap } from 'lucide-react'
import type { FishDef, FishStats } from '@/data/fish'
import { SegmentedBar } from './SegmentedBar'

interface StatsPanelProps {
  fish: FishDef
  stats: FishStats
  water: number
  onFeed: () => void
  onEnterFollow: () => void
  onLightShow: () => void
}

const ABILITY_CD = 3500

export function StatsPanel({ fish, stats, water, onFeed, onEnterFollow, onLightShow }: StatsPanelProps) {
  const [cd, setCd] = useState<Record<string, boolean>>({})
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), [])

  const trigger = (key: string, fn: () => void) => {
    if (cd[key]) return
    fn()
    setCd((c) => ({ ...c, [key]: true }))
    timers.current[key] = setTimeout(() => setCd((c) => ({ ...c, [key]: false })), ABILITY_CD)
  }

  const rows = [
    { icon: Drumstick, label: '饱食度', value: stats.satiety, color: '#f59e0b' },
    { icon: Heart, label: '心情值', value: stats.mood, color: '#f43f5e' },
    { icon: Hand, label: '亲密度', value: stats.bond, color: '#8b5cf6' },
    { icon: Zap, label: '活力值', value: stats.energy, color: '#10b981' },
    { icon: Droplets, label: '水质值', value: water, color: '#0ea5e9' },
  ]

  const abilities = [
    { key: 'feed', icon: Drumstick, name: '投喂点心', desc: '撒下鱼食,恢复饱食度。', run: onFeed },
    { key: 'tease', icon: Hand, name: '指尖逗鱼', desc: '进入逗鱼模式,它会追着你的指尖。', run: onEnterFollow },
    { key: 'light', icon: Sparkles, name: '光影秀', desc: '变幻灯光,大幅提升心情。', run: onLightShow },
  ]

  return (
    <div className="rounded-2xl bg-white/70 border border-white/80 backdrop-blur-md p-5 shadow-[0_6px_24px_rgba(70,140,220,0.15)]">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs font-black tracking-[0.25em] text-slate-400">状态面板</span>
        <span className="flex-1 h-px bg-slate-300/50" />
        <span className="text-[10px] tracking-widest text-slate-300 font-bold">UNIT STATS</span>
      </div>

      <div className="flex flex-col gap-3.5">
        {rows.map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="flex items-center gap-3">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1a`, color }}>
              <Icon size={15} />
            </span>
            <span className="text-sm font-semibold text-slate-600 w-14 shrink-0">{label}</span>
            <SegmentedBar value={value} color={color} />
            <span className="text-sm font-black text-slate-700 w-10 text-right tabular-nums">{value}</span>
          </div>
        ))}
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-slate-400/10 text-slate-400">
            <TriangleAlert size={15} />
          </span>
          <span className="text-sm font-semibold text-slate-600 w-14 shrink-0">饲养难度</span>
          <SegmentedBar value={fish.difficulty === '简单' ? 300 : fish.difficulty === '中等' ? 600 : 900} color="#94a3b8" />
          <span className="text-sm font-black text-slate-500 w-10 text-right">{fish.difficulty}</span>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-6 mb-4">
        <span className="text-xs font-black tracking-[0.25em] text-slate-400">互动技能</span>
        <span className="flex-1 h-px bg-slate-300/50" />
        <span className="text-[10px] tracking-widest text-slate-300 font-bold">ABILITIES</span>
      </div>

      <div className="flex flex-col gap-3">
        {abilities.map(({ key, icon: Icon, name, desc, run }) => (
          <button
            key={key}
            onClick={() => trigger(key, run)}
            disabled={cd[key]}
            className={`flex items-center gap-3.5 text-left rounded-xl p-2 -m-1 transition-all ${
              cd[key] ? 'opacity-40 cursor-not-allowed' : 'hover:bg-sky-50 active:scale-[0.98]'
            }`}
          >
            <span className="w-11 h-11 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center shadow-md shadow-sky-300/40 shrink-0">
              <Icon className="text-white" size={19} />
            </span>
            <span>
              <span className="block text-sm font-black tracking-wider text-slate-700">{name}</span>
              <span className="block text-xs text-slate-400 mt-0.5">{cd[key] ? '冷却中…' : desc}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
